import React from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import logo from '../../assets/logo.png'
import bgImage from '../../assets/bg.jpg'
import heroReminder from '../../assets/heroReminder.svg'

const Hero: React.FC = () => {
  const { scrollY } = useScroll()
  const y1 = useTransform(scrollY, [0, 500], [0, -100])
  const y2 = useTransform(scrollY, [0, 500], [0, -50])

  return (
    <section className='relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0a0a0a] flex flex-col items-center justify-center min-h-[90vh]'>
      {/* Background elements */}
      <div className='absolute inset-0 z-0 overflow-hidden pointer-events-none'>
        {/* Custom Background Image */}
        <div
          className='absolute inset-0 opacity-20'
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Soft Mesh Gradients */}
        <motion.div
          style={{ y: y1 }}
          className='absolute top-[-10%] right-[-5%] w-[45vw] h-[45vw] rounded-full bg-primary/20 mix-blend-screen filter blur-[100px] opacity-40 animate-blob'
        />
        <motion.div
          style={{ y: y2 }}
          className='absolute top-[20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-purple-900/20 mix-blend-screen filter blur-[100px] opacity-40 animate-blob animation-delay-2000'
        />
        <motion.div
          style={{ y: y1 }}
          className='absolute bottom-[-20%] left-[20%] w-[50vw] h-[50vw] rounded-full bg-primary/10 mix-blend-screen filter blur-[120px] opacity-40 animate-blob animation-delay-4000'
        />

        {/* Decorative elements representing productivity */}
        <div className='absolute top-1/4 left-[10%] hidden lg:block opacity-80 backdrop-blur-md rounded-2xl bg-[#1a1a1a] p-4 shadow-2xl transform -rotate-6 w-48 border border-[#262626]'>
          <p className='font-writing text-sm text-white'>
            Take notes to keep track of crucial details
          </p>
          <div className='absolute -top-3 left-1/2 w-4 h-4 bg-red-500 rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.5)] transform -translate-x-1/2' />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8, x: 20 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className='absolute top-[15%] right-[0%] hidden xl:block z-10 w-56 drop-shadow-xl'
        >
          <img src={heroReminder} alt='Reminder' className='w-full h-auto' />
        </motion.div>
      </div>

      <div className='relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className=' inline-flex p-1.5 rounded-full shadow-lg border border-[#1f1f1f] mb-2 bg-[#1a1a1a]'
        >
          <img
            src={logo}
            alt='ClickVoltz Logo'
            className=' w-12 object-contain rounded-xl '
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className='text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.1]'
        >
          Run your work.
          <br />
          <span className='text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600'>
            Without the chaos.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className='text-lg md:text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed mt-6'
        >
          Plan, track, and automate your workflows in one intelligent workspace.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className='flex flex-col sm:flex-row items-center justify-center gap-4 pt-6'
        >
          <button className='w-full sm:w-auto px-8 py-4 bg-primary text-black font-black rounded-full hover:bg-primary/90 transition-all duration-200 transform hover:scale-105 hover:shadow-[0_8px_20px_rgba(255,215,0,0.3)] focus:outline-none'>
            Get Started Free
          </button>
          <button className='w-full sm:w-auto px-8 py-4 bg-transparent text-white font-semibold rounded-full hover:bg-white/5 transition-all duration-200 transform hover:scale-105 shadow-sm border border-[#262626] hover:shadow-md focus:outline-none'>
            Book a Demo
          </button>
        </motion.div>
      </div>

      {/* Floating UI Elements Mockup at bottom of hero */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className='relative z-10 w-full max-w-6xl mx-auto mt-20 px-4'
      >
        <div className='relative rounded-t-2xl bg-[#1a1a1a]/60 backdrop-blur-xl border border-[#262626] shadow-[0_-20px_40px_rgba(0,0,0,0.5)] h-40 overflow-hidden translate-y-20'>
          {/* Minimal Mockup Header */}
          <div className='w-full h-12 bg-[#0a0a0a]/50 border-b border-[#262626] flex items-center px-4 gap-2'>
            <div className='w-3 h-3 rounded-full bg-red-500/50'></div>
            <div className='w-3 h-3 rounded-full bg-amber-500/50'></div>
            <div className='w-3 h-3 rounded-full bg-green-500/50'></div>
          </div>
          <div className='flex p-4 gap-4'>
            {/* Skeleton UI elements */}
            <div className='w-1/4 flex flex-col gap-3'>
              <div className='h-4 w-1/2 bg-[#262626] rounded-md'></div>
              <div className='h-8 w-full bg-[#262626] rounded-md'></div>
              <div className='h-8 w-full bg-[#262626] rounded-md'></div>
              <div className='h-8 w-full bg-[#262626] rounded-md'></div>
            </div>
            <div className='flex-1 flex flex-col gap-4'>
              <div className='h-6 w-1/3 bg-[#262626] rounded-md'></div>
              <div className='h-32 w-full bg-primary/5 rounded-lg border border-primary/10'></div>
            </div>
            <div className='w-1/4 flex flex-col gap-3'>
              <div className='h-full w-full bg-[#262626]/50 rounded-lg'></div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

export default Hero
